<template>
  <div v-if="!categoryData?.error">
    <div class="search-category max-w-screen-4xl mx-auto lg:px-4">
      <MissingTranslationNotification
        class="mt-8 px-4 lg:pl-0"
      />
      <div
        v-if="isCtListingLoading"
        class="pt-8 pb-4 lg:pb-0"
      >
        <SkeletonLoader
          height="48px"
          width="320px"
          item-classes="max-w-full mx-auto lg:mx-0"
        />
      </div>
      <PageTitle
        v-else
        data-testid="activeCategoryTitle"
        class="break-words"
        :page-title="activeCategory"
      />
      <CtSearchContainerSkeleton
        v-if="isCtListingLoading"
        show-breadcrumbs
      />
      <CtSearchContainer
        v-else-if="isCtProductSearchEnabled && currentCategory"
        :category-id="currentCategory.id"
        @product-listing-shown="onProductListingShown"
        @page-changed="onPageChanged"
      />
      <TypesenseContainer
        v-else-if="typesenseCategoryFilter"
        :category-filter="typesenseCategoryFilter"
        :show-parent-level="false"
        :language-and-country="languageAndCountry"
        description=""
        :product-group-items="[]"
        product-group-items-text-color="text-EXPONDO-black"
        product-group-items-background-color="bg-EXPONDO-grey-5"
        :enable-state-mapping="true"
        @product-listing-shown="onProductListingShown"
        @page-changed="onPageChanged"
      />
      <div
        v-if="!isCategoryLoading"
        class="px-5 lg:px-0"
      >
        <BlogSlider
          v-if="currentCategoryBlogPosts?.length"
          class="mt-4 mb-6"
          :posts="currentCategoryBlogPosts"
        />
        <DisplayHtmlDangerously
          v-if="formattedCategorySEODescription"
          :data-testid="CATEGORY_TEST_IDS.CATEGORY_SEO_DESCRIPTION"
          class="search-category__seo-description text-sm text-EXPONDO-grey"
          :html-text="formattedCategorySEODescription"
        />
      </div>
    </div>
    <RelatedCategoryTrees
      v-if="!isCategoryLoading && relatedTrees?.length"
      :data-testid="CATEGORY_TEST_IDS.RELATED_CATEGORY_SECTION"
      :related-trees="relatedTrees"
      class="bg-EXPONDO-grey-5 px-6 lg:px-10 pt-4 pb-6"
    />
  </div>
  <ErrorLayout
    v-else-if="categoryData?.error"
    :error="categoryData.error"
  />
</template>

<script lang="ts">
import { sendRedirect } from 'h3';
import ErrorLayout from '~/layouts/error.vue';
import type { HitResultItem } from 'vue-instantsearch/vue3/es';
import { getTypesenseCategoryFilter } from '~~/helpers/category/getTypesenseCategoryFilter';
import { getRelatedTrees } from '~~/helpers/category/relatedTree';
import type { RelatedTree } from '~~/types/category/RelatedCategory';
import { CacheTagPrefix } from '~/composables/useCache';
import { STATUS_RESOURCE_NOT_FOUND } from '~~/constants/http';
import { hasOnlyWhitelistedParams } from '~~/helpers/seo/whitelistedParameters';
import { getPageCategory } from '~~/helpers/seo/pageTitle';
import { CATEGORY_TEST_IDS } from '~~/constants/categoriesTestIds';

export default defineComponent({
  name: 'SearchCategory',
  components: {
    ErrorLayout,
  },
  props: {
    categorySlug: {
      type: String,
      required: true,
    },
  },
  async setup(props) {

    const { categorySlug } = toRefs(props);
    const route = useRoute();
    const { callPageViewEvent } = usePageViewEvent();
    const {
      categories,
      triggerViewCategoryEvent,
      currentCategory,
      searchWithDefaultParameters,
      isNewCategoryTree,
      getters: {
        currentCategorySlugAllLocales,
        currentCategoryMetaDescription,
        currentCategorySEODescription,
        currentCategoryBlogPosts,
      },
    } = useCategories();
    const { categoryTree } = useCategoryTree();

    const event = useRequestEvent();
    const { addTags } = useCache();
    const {
      languageAndCountry,
      isDefaultLanguageAndCountry,
      buildLanguagePrefixedUrlBasedOnActiveLanguage,
      countryCode,
    } = useLocalization();
    const formattedCategorySEODescription = computed(() =>
      isDefaultLanguageAndCountry.value
        ? currentCategorySEODescription.value
        : currentCategorySEODescription.value?.replace(
          /href="\//g, `href="${buildLanguagePrefixedUrlBasedOnActiveLanguage('/')}`),
    );

    const i18n = useI18n();
    const { seoRedirection } = useSeoRedirection();
    const typesenseCategoryFilter = useState(`typesense-category-filter-${categorySlug.value}`, () => '');
    const { isCtProductSearchEnabled } = useIsCtProductSearchEnabled();

    const onProductListingShown = (products: HitResultItem[]) => {

      triggerViewCategoryEvent(products, categorySlug.value);

    };

    const currentPage = ref(1);
    const onPageChanged = (newPage: number) => {

      currentPage.value = newPage;

    };

    const { generateActiveCategoryName, generateMetaTitle } = useCategoryPageTitle(currentPage);
    const activeCategory = computed(() => generateActiveCategoryName(typesenseCategoryFilter.value));

    const relatedTrees = useState<RelatedTree[]>('related-category-trees', () => []);

    const categoryData = ref<{ error: { statusCode: number } | null } | null>(null);

    const defaultMetaDescription = i18n.t(
      'SEO Category meta description', { categoryName: activeCategory.value },
    );

    const shouldBeIndexed = computed(() => hasOnlyWhitelistedParams(route.query));
    const metaTitle = computed(() => generateMetaTitle(activeCategory.value, categoryData.value?.error?.statusCode));

    onMounted(() => {

      callPageViewEvent({
        pageCategory: getPageCategory(
          isNewCategoryTree.value,
          categoryData.value?.error?.statusCode,
        ),
      });

    });

    useMetaExtended({
      metaTitle,
      isPageIndexed: shouldBeIndexed,
      metaDescription: computed(() => currentCategoryMetaDescription.value || defaultMetaDescription),
      slug: ref(categorySlug.value),
      localisedSlugs: currentCategorySlugAllLocales,
      isCategoryPage: true,
    });

    const setTypesenseCategoryFilter = async () => {

      await searchWithDefaultParameters(categorySlug.value);

      if (!currentCategory.value) {

        relatedTrees.value = [];
        typesenseCategoryFilter.value = '';

        return;

      }

      relatedTrees.value = getRelatedTrees(currentCategory.value, {
        languageAndCountry: languageAndCountry.value,
        countryCode: countryCode.value,
      });

      typesenseCategoryFilter.value = getTypesenseCategoryFilter({
        categorySlug: categorySlug.value,
        categories: categories.value,
        categoryTree: categoryTree.value,
      });

    };

    const categoryDataKey = `search-category-${categorySlug.value}`;
    const { data: asyncCategoryData, status: categoryStatus } = await useLazyAsyncData(categoryDataKey, async () => {

      try {

        await setTypesenseCategoryFilter();
        const page = Number(route.query?.page) || 1;
        currentPage.value = page;

        if (typesenseCategoryFilter.value) {

          addTags([
            { prefix: CacheTagPrefix.View, value: 'searchcategory' },
            { prefix: CacheTagPrefix.Block, value: props.categorySlug },
          ]);

        } else {

          const seoRedirect = await seoRedirection();

          if (seoRedirect) {

            if (event) {

              return sendRedirect(event, seoRedirect.to, seoRedirect.redirectCode);

            }

            return navigateTo(seoRedirect.to, {
              redirectCode: seoRedirect.redirectCode,
              external: seoRedirect.external,
            });

          }

          if (event) {

            setResponseStatus(event, STATUS_RESOURCE_NOT_FOUND);

          }

          const result = { error: { statusCode: STATUS_RESOURCE_NOT_FOUND } };

          categoryData.value = result;

          return result;

        }

      } catch (e) {

        console.error('error during category search', e);

      }

      const result = { error: null };

      categoryData.value = result;

      return result;

    });

    categoryData.value = asyncCategoryData.value ?? null;

    // While the category loads, the shared category state still holds the previously visited one.
    const isCategoryLoading = computed(() => categoryStatus.value === 'idle' || categoryStatus.value === 'pending');
    const isCtListingLoading = computed(() => isCtProductSearchEnabled.value && isCategoryLoading.value);

    return {
      relatedTrees,
      typesenseCategoryFilter,
      currentCategory,
      isCategoryLoading,
      isCtListingLoading,
      isCtProductSearchEnabled,
      onProductListingShown,
      onPageChanged,
      activeCategory,
      formattedCategorySEODescription,
      currentCategoryMetaDescription,
      languageAndCountry,
      currentCategoryBlogPosts,
      categoryData,
      CATEGORY_TEST_IDS,
    };

  },
});

</script>

<style lang="scss">
.search-category__seo-description {
  margin-bottom: 2rem;

  & > h1,
  & > h2,
  & > h3,
  & > h4,
  & > h5,
  & > h6 {
    margin-top: 1.25rem;
  }

  ul,
  ol {
    margin: 0.5rem 0;
    padding-left: 0.8rem;
  }

  a {
    @apply text-EXPONDO-secondary underline;
  }
}
</style>
